import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";

/**
 * ADP-167: the main/sub-account model gives every user exactly one Workspace,
 * so the former switcher dropdown (+ create-workspace dialog) collapsed into a
 * static label that keeps the sidebar's visual slot.
 */
export function WorkspaceSelect({
  collapsed = false,
}: {
  collapsed?: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation("layout");
  const user = useAuthStore((state) => state.user);
  const workspaces = user?.workspaces ?? [];
  const active =
    workspaces.find((workspace) => workspace.id === user?.activeWorkspaceId) ?? workspaces[0];

  if (!active) return null;

  const content = (
    <div
      className={cn(
        "flex items-center rounded-[10px] border border-transparent bg-transparent text-left transition-colors duration-150 ease-out hover:border-line hover:bg-white motion-reduce:transition-none",
        collapsed ? "size-10 justify-center p-1.5" : "h-[52px] w-full gap-[9px] p-2",
      )}
    >
      <WorkspaceInitials name={active.name} />
      {!collapsed ? (
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="truncate text-[12px] font-semibold leading-4">{active.name}</span>
          <span className="truncate text-[10px] leading-3.5 text-muted-foreground">
            {roleLabel(t, active.role)} · {formatShopCount(t, active.shopCount)}
          </span>
        </span>
      ) : null}
    </div>
  );

  if (!collapsed) return content;
  return (
    <Tooltip>
      <TooltipTrigger render={content} />
      <TooltipContent side="right">{active.name}</TooltipContent>
    </Tooltip>
  );
}

function WorkspaceInitials({ name }: { name: string }): React.ReactElement {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return (
    <span className="flex size-7 shrink-0 items-center justify-center rounded-[8px] bg-control-hover text-[10px] font-semibold text-foreground">
      {initials || "WS"}
    </span>
  );
}

function formatShopCount(t: TFunction<"layout">, count: number): string {
  return t("workspace.shopCount", { count });
}

function roleLabel(t: TFunction<"layout">, role: "owner" | "admin" | "member"): string {
  return t(`workspace.roles.${role}`);
}
